import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import API from '../api';
import Modal from './Modal';

const EMPTY = { title: '', subtitle: '', title_ja: '', subtitle_ja: '', image_url: '', sort_order: 0, is_active: 1 };
const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';

export default function AdminHero() {
  const [slides, setSlides] = useState([]);
  const [modal, setModal] = useState(null); // null | { mode:'add'|'edit', data }
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => API.get('/hero/all').then(r => setSlides(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    const nextOrder = slides.length ? Math.max(...slides.map(s => s.sort_order)) + 1 : 0;
    setForm({ ...EMPTY, sort_order: nextOrder });
    setModal({ mode: 'add' });
  };
  const openEdit = (s) => { setForm({ ...s }); setModal({ mode: 'edit', id: s.id }); };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image_url: data.url }));
    } catch { alert('Upload failed'); }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'add') await API.post('/hero', form);
      else await API.put(`/hero/${modal.id}`, form);
      setModal(null);
      load();
    } catch { alert('Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this slide?')) return;
    await API.delete(`/hero/${id}`);
    load();
  };

  const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-primary-dark">Hero Slides</h1>
          <p className="text-sm text-gray-500 mt-1">Manage the homepage slideshow</p>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm flex items-center gap-2">
          <PlusIcon className="w-4 h-4" /> Add Slide
        </button>
      </div>

      <div className="space-y-4">
        {slides.length === 0 && (
          <div className="bg-white border border-dashed border-gray-300 p-12 text-center text-gray-400">
            No slides yet. Click "Add Slide" to get started.
          </div>
        )}
        {slides.map((s, i) => (
          <div key={s.id} className="bg-white border border-gray-100 shadow-sm flex items-center gap-4 p-4">
            <span className="text-xs text-gray-400 font-mono w-5 text-center">{i + 1}</span>
            <div className="w-32 h-18 bg-gray-100 shrink-0 overflow-hidden" style={{ height: '72px' }}>
              {s.image_url ? (
                <img src={imgSrc(s.image_url)} alt={s.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary/40 text-xs">No Image</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`font-semibold truncate ${s.title ? 'text-gray-800' : 'text-gray-400 italic font-normal'}`}>
                {s.title || 'Image only, no text overlay'}
              </div>
              {s.subtitle && <div className="text-sm text-gray-500">{s.subtitle}</div>}
              <div className={`text-xs mt-1 font-medium ${s.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                {s.is_active ? '● Active' : '○ Hidden'}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(s)} className="p-2 text-gray-400 hover:text-primary border border-gray-200 hover:border-primary transition-colors">
                <PencilIcon className="w-4 h-4" />
              </button>
              <button onClick={() => handleDelete(s.id)} className="p-2 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors">
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <Modal title={modal.mode === 'add' ? 'Add Hero Slide' : 'Edit Hero Slide'} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Title</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              <p className="text-xs text-gray-400 mt-1">Leave blank for an image-only slide with no text overlay — e.g. a graphic that already has its own text baked in.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Title (Japanese)</label>
              <input value={form.title_ja || ''} onChange={e => setForm(f => ({ ...f, title_ja: e.target.value }))}
                placeholder="Falls back to Title if left blank"
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Subtitle</label>
              <input value={form.subtitle || ''} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Subtitle (Japanese)</label>
              <input value={form.subtitle_ja || ''} onChange={e => setForm(f => ({ ...f, subtitle_ja: e.target.value }))}
                placeholder="Falls back to Subtitle if left blank"
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Image</label>
              <div className="space-y-2">
                {form.image_url && (
                  <img src={imgSrc(form.image_url)} alt="preview" className="h-32 w-full object-cover border border-gray-200" />
                )}
                <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading}
                  className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:border-0 file:bg-primary file:text-white file:text-xs file:cursor-pointer" />
                {uploading && <p className="text-xs text-primary">Uploading...</p>}
                <p className="text-xs text-gray-400">Or paste a URL:</p>
                <input value={form.image_url || ''} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
                <select value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  <option value={1}>Active</option>
                  <option value={0}>Hidden</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving || !form.image_url}
                className="btn-primary text-sm disabled:opacity-60 flex-1">
                {saving ? 'Saving...' : 'Save Slide'}
              </button>
              <button onClick={() => setModal(null)} className="btn-outline text-sm flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
